import { useState, useEffect } from "react";
import ReactTable from "components/lib/Global/ReactTable";
import { useDispatch, useSelector } from "react-redux";
import { getCorporates } from "state/slices/contactPersonCorporates";
import { useDebounce } from "use-debounce";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import SearchInput from "components/lib/Shared/SearchInput";
import { getCorporatesForDashboard } from "state/slices/corporatesForDashboard";
import Cookie from "js-cookie";
import { Constants } from "config/constants";
import { format } from "date-fns";

const Subscribers = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);
  const cookieRole = Cookie.get("role");

  const {
    items,
    totalCount: orgsTotalCount,
    getOrganizations,
  } = useSelector((state) => state.contactPersonCorporatesSlice);
  const { organization, organizationName } = useSelector(
    (state) => state.activeDashboardSlice
  );
  const { corporates, getCorporatesForDashboardStatus } = useSelector(
    (state) => state.corporatesForDashboardSlice
  );

  const dispatch = useDispatch();
  let subscribersData = [];
  let totalCount = 0;
  let tableData = [];

  if (cookieRole === Constants.COOKIE_ROLES.SUPER_ADMIN) {
    totalCount = orgsTotalCount;
    tableData = items;
  } else {
    totalCount = corporates.totalCount;
    tableData = corporates.items;
  }

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  useEffect(() => {
    if (debouncedSearchTerm) setCurrentPage(1);

    if (cookieRole === Constants.COOKIE_ROLES.SUPER_ADMIN) {
      dispatch(
        getCorporates({
          queryParams: {
            search: debouncedSearchTerm,
            page: "corporate-directors-shareholders",
            limit: rowsPerPage,
            skip: (currentPage - 1) * rowsPerPage,
            order: "desc",
            subscribers: true,
          },
        })
      );
    } else {
      dispatch(
        getCorporatesForDashboard({
          companyRegNumber: organization?.names?.companyRegNumber,
          companyDashboard: !organization?.partnershipType,
          search: debouncedSearchTerm,
          limit: rowsPerPage,
          skip: (currentPage - 1) * rowsPerPage,
          order: "asc",
          subscribers: true,
        })
      );
    }
  }, [rowsPerPage, currentPage, debouncedSearchTerm]);

  const columns = [
    {
      Header: "Organization",
      accessor: "organization",
      Cell: ({ row }) => {
        if (row.original?.names?.companyName) {
          return row.original?.names?.companyName;
        }
        if (row.original?.registrationName) {
          return row.original.registrationName;
        }
        if (row.original?.companyName1) {
          return row.original.companyName1;
        }
        return "-";
      },
    },
    {
      Header: "Plan",
      accessor: "subscriptionPlan",
      Cell: ({ row }) => {
        if (row.original?.activeCustomerSubscription?.subscription) {
          return row.original.activeCustomerSubscription.subscription.name;
        }
        return "-";
      },
    },
    {
      Header: "Billing Date",
      accessor: "billingDate",
      Cell: ({ row }) => {
        if (row.original?.activeCustomerSubscription?.nextBillingDate) {
          return format(
            new Date(row.original.activeCustomerSubscription.nextBillingDate),
            "dd/MM/yyyy"
          );
        }
        return "-";
      },
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }) => {
        return (
          <div className="bg-green-300 text-green-900 rounded-full p-1 text-center items-center">
            Active
          </div>
        );
      },
    },
    {
      Header: "Total Amount (KES)",
      accessor: "totalAmount",
      Cell: ({ row }) => {
        if (row.original?.activeCustomerSubscription?.subscription) {
          return row.original.activeCustomerSubscription.subscription
            .currencies[0].basePrice;
        }
        return "-";
      },
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between my-2">
        <div className="flex flex-col gap-1">
          <h2 className="text-primary font-semibold">Subscribers</h2>
          <p className="text-sm text-gray-500">
            Organization administrators subscribed to the platform
          </p>
        </div>
        <SearchInput value={searchTerm} handleOnChange={handleSearch} />
      </div>

      <div>{getOrganizations.status === "loading" && <SpinnerIcon />}</div>

      <div>
        {totalCount === 0 ? (
          <p className="text-center text-gray-500">No subscribers found.</p>
        ) : (
          <ReactTable
            columns={columns}
            data={tableData}
            rowsPerPage={rowsPerPage}
            totalRows={totalCount}
            setCurrentPage={setCurrentPage}
            paginate
            dataRowCellStyle="pl-4 leading-snug py-2"
          />
        )}
      </div>
    </div>
  );
};

export default Subscribers;
